import React, {type ReactElement} from "react";

import {LOCALE} from "/src/config";
import {MaybeType} from "/src/types";
import {css, localizeSlug, visibleForLocale} from "/src/utils/utils";
import translate from "/src/utils/translate";
import flexportSocialData from "/src/data/flexport-socials";

import SocialIcons from "../social-icons/SocialIcons";
import useHasWindow from "/src/hooks/useHasWindow";
import ChevronArrow from "/src/svg/chevron-arrow.svg";
import Expandable from "../accordion/expandable/Expandable";
import FlexportLight from "/src/svg/flexport-light.svg";
import Icon from "../icon/Icon";
import LinkTag from "../buttons/link-tag/LinkTag";
import ModuleBody from "../layouts/module-body/ModuleBody";

import * as styles from "./footer.module.scss";

interface FooterProps {
  navTabs: Array<Queries.NavTabLevelFragmentFragment>;
  hideNav?: boolean;
  hidePrivacyPolicy?: boolean;
}

/**
 * Internal component.
 */
const CollapseIcon = () => (
  <Icon
    classes={styles.footer__accordion__iconCollapsed}
    svg={ChevronArrow}
    role="presentation"
    data-state="collapsed"
  />
);

/**
 * Internal component.
 */
const ExpandIcon = () => (
  <Icon
    classes={styles.footer__accordion__iconExpanded}
    svg={ChevronArrow}
    role="presentation"
    data-state="expanded"
  />
);

const compileSubLinks = (
  column1SubItems: MaybeType<Array<Queries.SubColumnItemsFragmentFragment>>,
  column2SubItems: MaybeType<Array<Queries.SubColumnItemsFragmentFragment>>,
  column3SubItems: MaybeType<Array<Queries.SubColumnItemsFragmentFragment>>,
  column4SubItems: MaybeType<Array<Queries.SubColumnItemsFragmentFragment>>
) => {
  const column1Link =
    column1SubItems && column1SubItems.length ? column1SubItems : [];
  const column2Link =
    column2SubItems && column2SubItems.length ? column2SubItems : [];
  const column3Link =
    column3SubItems && column3SubItems.length ? column3SubItems : [];
  const column4Link =
    column4SubItems && column4SubItems.length ? column4SubItems : [];

  const columnLinks = [
    ...column1Link,
    ...column2Link,
    ...column3Link,
    ...column4Link,
  ].filter(item => {
    const itemVisibleIn: string[] =
      item && item.visibleIn ? (item.visibleIn as Array<string>) : [];

    if (
      item.menuItemLink == null ||
      item.menuItemTitle == null ||
      !visibleForLocale(itemVisibleIn, LOCALE)
    ) {
      return false;
    }

    return true;
  });

  return columnLinks;
};

const expandableClasses = {
  item: styles.footer__accordion__item,
  itemOpened: styles.footer__accordion__itemOpened,
  title: styles.footer__accordion__title,
  titleClosed: styles.footer__accordion__titleClosed,
  titleOpened: styles.footer__accordion__titleOpened,
};

// separating this as micro component to perform partial hydation
// ref: https://www.gatsbyjs.com/docs/how-to/performance/partial-hydration/#when-to-use-client-components
const PrivacyPolicyCopy = (props: FooterProps) => {
  const hasWindow = useHasWindow();
  const {hidePrivacyPolicy} = props;

  if (!hasWindow) {
    return null;
  }

  return (
    <>
      <p className={css(styles.footer__footnote__text)}>
        {translate(
          "This site is protected by reCAPTCHA Enterprise and the Google"
        )}{" "}
        <LinkTag
          classes={styles.footer__footnote__link}
          href="https://policies.google.com/privacy"
          openInNewTab
        >
          {translate("Privacy Policy")}
        </LinkTag>{" "}
        {translate("and")}{" "}
        <LinkTag
          classes={styles.footer__footnote__link}
          href="https://policies.google.com/terms"
          openInNewTab
        >
          {translate("Terms of Service")}
        </LinkTag>{" "}
        {translate("apply.")}
      </p>
      <p className={css("p-small", styles.footer__footnote__text)}>
        {translate("Copyright © {{year}} Flexport, Inc.", {
          year: new Date().getFullYear(),
        })}{" "}
        {" / "}
        <LinkTag
          classes={css(styles.footer__footnote__link)}
          href="https://beian.miit.gov.cn/"
          openInNewTab
        >
          {translate("Shanghai ICP No. 16041494-1")}
        </LinkTag>
        {hidePrivacyPolicy !== true && (
          <>
            {" / "}
            <LinkTag
              classes={styles.footer__footnote__link}
              href={localizeSlug("/terms-and-conditions", LOCALE)}
            >
              {translate("Terms of Use")}
            </LinkTag>{" "}
            {" / "}
            <LinkTag
              classes={styles.footer__footnote__link}
              href={localizeSlug("/privacy", LOCALE)}
            >
              {translate("Privacy Policy")}
            </LinkTag>{" "}
            {" / "}
            <LinkTag
              classes={styles.footer__footnote__link}
              href={"#"}
              // @ts-ignore
              onClick={() => window.semaphore.push(["showPreferences"])}
            >
              {translate("Privacy Settings")}
            </LinkTag>
          </>
        )}
      </p>
    </>
  );
};

const Footer = (props: FooterProps): ReactElement | null => {
  const [accordionOpenIndexState, setAccordionOpenIndexState] =
    React.useState(-1);
  const {navTabs, hideNav} = props;

  const baseKeyValue = "footer-nav";

  const mobileNavItems: Array<ReactElement> = [];

  return (
    <footer
      className={css(
        "content-inertable",
        styles.footer,
        hideNav === true ? styles.footerNoNav : ""
      )}
    >
      <ModuleBody classes={css("grid", styles.footer__layout)}>
        <div className={css(styles.footer__logoWrapper)}>
          <LinkTag ariaLabel="Flexport" href="/">
            <Icon
              classes={styles.footer__logo}
              svg={FlexportLight}
              role="presentation"
            />
          </LinkTag>
        </div>
        {hideNav !== true &&
          navTabs.map((navTab, i) => {
            const {
              tabTitle,
              url,
              column1SubItems,
              column2SubItems,
              column3SubItems,
              column4SubItems,
            } = navTab;

            const columnLinks = compileSubLinks(
              column1SubItems as MaybeType<
                Array<Queries.SubColumnItemsFragmentFragment>
              >,
              column2SubItems as MaybeType<
                Array<Queries.SubColumnItemsFragmentFragment>
              >,
              column3SubItems as MaybeType<
                Array<Queries.SubColumnItemsFragmentFragment>
              >,
              column4SubItems as MaybeType<
                Array<Queries.SubColumnItemsFragmentFragment>
              >
            );

            const groupKey = `${baseKeyValue}-group-${i}`;

            const headerText = (
              <h4 className={styles.footer__menuGroup__title}>{tabTitle}</h4>
            );

            const header =
              url != null ? (
                <LinkTag href={url!} openInNewTab>
                  {headerText}
                </LinkTag>
              ) : (
                headerText
              );

            const menuLinks = columnLinks.length ? (
              <ul className={styles.footer__menuGroup__ul}>
                {columnLinks.map((item, j) => (
                  <li
                    className={styles.footer__menuGroup__li}
                    key={`${groupKey}-link-${j}`}
                  >
                    <LinkTag
                      classes={css(styles.footer__menuGroup__link)}
                      href={item.menuItemLink!}
                    >
                      {item.menuItemTitle}
                    </LinkTag>
                  </li>
                ))}
              </ul>
            ) : null;

            mobileNavItems.push(
              <div
                key={`mobile-${groupKey}`}
                className={css(styles.footer__menuGroup)}
              >
                <Expandable
                  id={`footer-mobile-nav-${i}`}
                  title={header}
                  classes={expandableClasses}
                  iconOpened={<CollapseIcon />}
                  iconClosed={<ExpandIcon />}
                  content={menuLinks!}
                  key={`${baseKeyValue}-mobile-nav-group-${i}`}
                  open={accordionOpenIndexState === i}
                  onClick={expanded => {
                    if (expanded) {
                      setAccordionOpenIndexState(i);
                    }
                  }}
                />
              </div>
            );

            return (
              <div
                key={groupKey}
                className={css(
                  styles.footer__menuGroup,
                  styles.footer__menuGroupDesktop
                )}
              >
                {header}
                {menuLinks}
              </div>
            );
          })}
      </ModuleBody>
      {/* mobile nav, because it's full bleed, shouldn't be wrap with module body */}
      {hideNav !== true && (
        <div className={styles.footer__mobileNav}>
          {mobileNavItems.map(item => item)}
        </div>
      )}

      <ModuleBody
        classes={css("grid", styles.footer__layout, styles.footer__footnote)}
      >
        <div className={styles.footer__footnoteLeft}>
          <SocialIcons id="footer-socials" iconList={flexportSocialData} />
        </div>
        <div className={styles.footer__footnoteRight}>
          <PrivacyPolicyCopy {...props} />
        </div>
      </ModuleBody>
    </footer>
  );
};

export default Footer;
